import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import {
  splitVariants,
  splitItemVariants,
  type SplitVariantProps,
  type SplitItemVariantProps,
} from './split.variants';

export type SplitRatio =
  | 'equal'
  | '1/3-2/3'
  | '2/3-1/3'
  | '1/4-3/4'
  | '3/4-1/4';

export interface SplitProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Omit<SplitVariantProps, 'reverse'> {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /** Column width ratio */
  ratio?: SplitRatio;
  /** Reverse order on desktop */
  reverse?: boolean;
}

export interface SplitItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
}

/**
 * Split - Responsive two-column layout component
 *
 * Replaces manual `<div class="flex flex-col md:flex-row gap-*">` patterns.
 * Use Split for side-by-side content that stacks on mobile.
 *
 * @example Basic usage
 * ```tsx
 * <Split gap="3xl" ratio="equal">
 *   <SplitItem>
 *     <ImageCard ... />
 *   </SplitItem>
 *   <SplitItem>
 *     <ImageCard ... />
 *   </SplitItem>
 * </Split>
 * ```
 *
 * @example With ratio
 * ```tsx
 * <Split gap="xl" ratio="1/3-2/3">
 *   <SplitItem>
 *     <Sidebar />
 *   </SplitItem>
 *   <SplitItem>
 *     <MainContent />
 *   </SplitItem>
 * </Split>
 * ```
 *
 * @example Reversed order on desktop
 * ```tsx
 * <Split gap="2xl" reverse>
 *   <SplitItem>Content on right (desktop)</SplitItem>
 *   <SplitItem>Content on left (desktop)</SplitItem>
 * </Split>
 * ```
 */
export const Split = React.forwardRef<HTMLDivElement, SplitProps>(
  (
    {
      className,
      gap = 'xl',
      breakpoint = 'md',
      ratio = 'equal',
      reverse = false,
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';

    // Wrap children in divs with ratio classes (instead of cloning)
    // This ensures ratio classes work with shortcode-rendered children
    const childrenWithRatio = React.Children.map(children, (child, index) => {
      let ratioVariant: string = 'equal';
      if (ratio !== 'equal') {
        ratioVariant = `${ratio}-${index === 0 ? 'first' : 'second'}`;
      }
      return (
        <div
          className={splitItemVariants({
            ratio: ratioVariant as SplitItemVariantProps['ratio'],
          })}
        >
          {child}
        </div>
      );
    });

    return (
      <Comp
        className={cn(splitVariants({ gap, breakpoint, reverse }), className)}
        ref={ref}
        {...props}
      >
        {childrenWithRatio}
      </Comp>
    );
  },
);

Split.displayName = 'Split';

/**
 * SplitItem - Child component for Split layout
 *
 * Use SplitItem as direct children of Split to define the two columns.
 */
export const SplitItem = React.forwardRef<HTMLDivElement, SplitItemProps>(
  ({ className, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp className={className} ref={ref} {...props}>
        {children}
      </Comp>
    );
  },
);

SplitItem.displayName = 'SplitItem';
